import { get } from '@aiptyg/api-client';
import {
  Panel,
  Notice,
  input,
  button,
  useFilters,
  useResource,
} from '../features/agency/ui';
import { query } from '../features/agency/service';
import { formatDateTime } from '../lib/format';
interface Report {
  as_of: string;
  timezone: string;
  from: string;
  to: string;
  coverage: {
    note: string;
    historical_attempts_complete: boolean;
    event_recording_started_at?: string;
  };
  overview: {
    claimed_people: number;
    claimed_shares: number;
    submitted_shares: number;
    submission_attempts: number;
    approved_shares: number;
    pending_review_shares: number;
  };
  progress: {
    task_id: number;
    title: string;
    target_shares: number;
    claimed_shares: number;
    approved_shares: number;
    completion_rate: number | null;
  }[];
  daily_trends: {
    date: string;
    claimed_shares: number;
    submitted_attempts: number;
    approved_attempts: number;
    rejected_attempts: number;
    reward_minor: number;
  }[];
  cohort_funnel: {
    claimed_shares: number;
    submitted_shares: number;
    approved_shares: number;
    rewarded_shares: number;
    submit_rate: number | null;
    approval_rate: number | null;
  };
  quality: {
    first_reviewed_shares: number;
    first_approved_shares: number;
    first_approval_rate: number | null;
    final_reviewed_shares: number;
    final_approved_shares: number;
    final_approval_rate: number | null;
  };
  review_latency: {
    sample_attempts: number;
    median_seconds: number | null;
    p90_seconds: number | null;
  };
  rewards: { credited_shares: number; member_task_reward_minor: number };
  exceptions: {
    overdue_shares: number;
    rejected_shares: number;
    cancelled_shares: number;
    near_deadline_shares: number;
  };
}
const percent = (v: number | null | undefined) =>
  v == null ? '—' : `${(v * 100).toFixed(1)}%`;
const duration = (v: number | null) =>
  v == null
    ? '—'
    : v < 3600
      ? `${(v / 60).toFixed(1)} 分钟`
      : `${(v / 3600).toFixed(1)} 小时`;
function Tile({
  name,
  value,
  note,
}: {
  name: string;
  value: string | number | undefined;
  note?: string;
}) {
  return (
    <div className="rounded-md border border-gray-200 p-4">
      <p className="text-xs text-gray-500">{name}</p>
      <p className="my-2 text-2xl font-semibold tabular-nums">{value ?? '—'}</p>
      {note && <p className="text-xs text-gray-500">{note}</p>}
    </div>
  );
}
function TrendChart({ days }: { days: Report['daily_trends'] }) {
  if (!days?.length) return null;
  const max = Math.max(
    1,
    ...days.flatMap((x) => [
      x.claimed_shares,
      x.submitted_attempts,
      x.approved_attempts,
    ]),
  );
  const series = [
    { label: '领取份数', color: '#2563eb', key: 'claimed_shares' },
    { label: '提交尝试', color: '#d97706', key: 'submitted_attempts' },
    { label: '通过尝试', color: '#059669', key: 'approved_attempts' },
  ] as const;
  const step = 620 / Math.max(1, days.length - 1);
  return (
    <div className="rounded-md border border-gray-100 p-3">
      <div className="flex flex-wrap gap-5 text-xs">
        {series.map((x) => (
          <span key={x.key} style={{ color: x.color }}>
            ● {x.label}
          </span>
        ))}
      </div>
      <svg
        viewBox="0 0 680 210"
        role="img"
        aria-label="每日领取、提交和通过趋势"
        className="w-full max-h-64"
      >
        <path d="M40 15V175H660" stroke="#e5e7eb" fill="none" />
        <text x="3" y="22" fontSize="11" fill="#6b7280">
          {max}
        </text>
        <text x="25" y="178" fontSize="11" fill="#6b7280">
          0
        </text>
        {series.map((x) => (
          <polyline
            key={x.key}
            fill="none"
            stroke={x.color}
            strokeWidth="2.5"
            points={days
              .map((d, i) => `${40 + i * step},${175 - (d[x.key] / max) * 155}`)
              .join(' ')}
          />
        ))}
        <text x="40" y="199" fontSize="11" fill="#6b7280">
          {days[0].date}
        </text>
        <text x="660" y="199" textAnchor="end" fontSize="11" fill="#6b7280">
          {days[days.length - 1].date}
        </text>
      </svg>
    </div>
  );
}
export function AgentReportsPage() {
  const { q, set } = useFilters();
  const params = {
    agent_id: q.get('agent_id') ?? '',
    task_id: q.get('task_id') ?? '',
    from: q.get('from') ?? '',
    to: q.get('to') ?? '',
    timezone: 'Asia/Shanghai',
  };
  const apiParams = {
    ...params,
    from: params.from ? `${params.from}T00:00:00+08:00` : '',
    to: params.to
      ? new Date(
          new Date(`${params.to}T00:00:00+08:00`).getTime() + 24 * 3600 * 1000,
        ).toISOString()
      : '',
  };
  const r = useResource(
    () => get<Report>(`/v1/admin/agent-reports?${query(apiParams)}`),
    [q.toString()],
  );
  const global = useResource(
    () =>
      params.agent_id
        ? get<Report>(
            `/v1/admin/agent-reports?${query({ ...apiParams, agent_id: '', task_id: '' })}`,
          )
        : Promise.resolve(undefined),
    [q.toString()],
  );
  const d = r.data;
  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-500">
        中国区商户任务在线报表，统计会员任务奖励。人数、份数、提交尝试分别计算。
      </p>
      <Panel>
        <div className="flex flex-wrap gap-3">
          <input
            aria-label="商户 ID"
            className={input}
            placeholder="商户 ID（空为全部商户）"
            value={params.agent_id}
            onChange={(e) => set('agent_id', e.target.value)}
          />
          <input
            aria-label="商户任务 ID"
            className={input}
            placeholder="商户任务 ID"
            value={params.task_id}
            onChange={(e) => set('task_id', e.target.value)}
          />
          <label className="text-sm text-gray-500">
            开始{' '}
            <input
              aria-label="统计开始日期"
              type="date"
              className={input}
              value={params.from}
              onChange={(e) => set('from', e.target.value)}
            />
          </label>
          <label className="text-sm text-gray-500">
            结束{' '}
            <input
              aria-label="统计结束日期"
              type="date"
              className={input}
              value={params.to}
              onChange={(e) => set('to', e.target.value)}
            />
          </label>
          <button
            className={button}
            onClick={() => {
              r.reload();
              global.reload();
            }}
          >
            刷新
          </button>
        </div>
      </Panel>
      <Notice error={r.error || global.error} />
      {r.loading && <p className="text-gray-400 text-sm">统计加载中…</p>}
      {d && (
        <>
          <p className="text-xs text-gray-500">
            统计截至 {formatDateTime(d.as_of)} · {d.timezone} ·{' '}
            {d.coverage.note}
            {d.coverage.event_recording_started_at
              ? ` · 事件记录开始 ${formatDateTime(d.coverage.event_recording_started_at)}`
              : ''}
          </p>
          <Panel title="1. 任务总览">
            <div className="grid grid-cols-2 xl:grid-cols-6 gap-3">
              <Tile name="领取人数" value={d.overview.claimed_people} />
              <Tile name="领取份数" value={d.overview.claimed_shares} />
              <Tile name="已提交份数" value={d.overview.submitted_shares} />
              <Tile
                name="提交尝试次数"
                value={d.overview.submission_attempts}
              />
              <Tile name="通过份数" value={d.overview.approved_shares} />
              <Tile
                name="待审核份数"
                value={d.overview.pending_review_shares}
              />
            </div>
          </Panel>
          {global.data && (
            <Panel title="同周期全部商户对比">
              <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
                <Tile
                  name="全局领取人数"
                  value={global.data.overview.claimed_people}
                />
                <Tile
                  name="全局领取份数"
                  value={global.data.overview.claimed_shares}
                />
                <Tile
                  name="全局通过份数"
                  value={global.data.overview.approved_shares}
                />
                <Tile
                  name="本商户通过份数占比"
                  value={
                    global.data.overview.approved_shares
                      ? percent(
                          d.overview.approved_shares /
                            global.data.overview.approved_shares,
                        )
                      : '—'
                  }
                  note="全部商户在同一统计周期内的通过份数"
                />
              </div>
            </Panel>
          )}
          <Panel title="2. 任务完成进度">
            <div className="overflow-auto">
              <table className="w-full min-w-[720px] text-sm text-left">
                <thead className="bg-gray-50">
                  <tr>
                    {['任务', '目标份数', '领取份数', '通过份数', '完成率'].map(
                      (x) => (
                        <th className="p-3" key={x}>
                          {x}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {d.progress?.map((x) => (
                    <tr key={x.task_id} className="border-b border-gray-100">
                      <td className="p-3">
                        #{x.task_id} {x.title}
                      </td>
                      <td className="p-3">{x.target_shares}</td>
                      <td className="p-3">{x.claimed_shares}</td>
                      <td className="p-3">{x.approved_shares}</td>
                      <td className="p-3">{percent(x.completion_rate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!d.progress?.length && (
                <p className="py-6 text-center text-gray-400">暂无任务数据</p>
              )}
            </div>
          </Panel>
          <Panel title="3. 每日趋势">
            <TrendChart days={d.daily_trends} />
            <div className="overflow-auto">
              <table className="w-full min-w-[720px] text-sm text-left">
                <thead className="bg-gray-50">
                  <tr>
                    {[
                      '日期',
                      '领取份数',
                      '提交尝试',
                      '通过尝试',
                      '驳回尝试',
                      '会员任务奖励',
                    ].map((x) => (
                      <th className="p-3" key={x}>
                        {x}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {d.daily_trends?.map((x) => (
                    <tr className="border-b border-gray-100" key={x.date}>
                      <td className="p-3">{x.date}</td>
                      <td className="p-3">{x.claimed_shares}</td>
                      <td className="p-3">{x.submitted_attempts}</td>
                      <td className="p-3">{x.approved_attempts}</td>
                      <td className="p-3">{x.rejected_attempts}</td>
                      <td className="p-3">
                        ¥{(x.reward_minor / 100).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!d.daily_trends?.length && (
                <p className="py-6 text-center text-gray-400">暂无趋势数据</p>
              )}
            </div>
          </Panel>
          <Panel title="4. 领取批次转化漏斗">
            <p className="text-xs text-gray-500">
              按统计周期内领取的任务份数追踪后续提交、通过与入账；分母为同一领取批次。
            </p>
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
              <Tile name="领取" value={d.cohort_funnel.claimed_shares} />
              <Tile
                name="提交"
                value={d.cohort_funnel.submitted_shares}
                note={`提交率 ${percent(d.cohort_funnel.submit_rate)}`}
              />
              <Tile
                name="通过"
                value={d.cohort_funnel.approved_shares}
                note={`通过率 ${percent(d.cohort_funnel.approval_rate)}`}
              />
              <Tile name="奖励入账" value={d.cohort_funnel.rewarded_shares} />
            </div>
          </Panel>
          <Panel title="5. 提交质量">
            <div className="grid grid-cols-2 gap-3">
              <Tile
                name="首次审核通过率"
                value={percent(d.quality.first_approval_rate)}
                note={`${d.quality.first_approved_shares} / ${d.quality.first_reviewed_shares} 份`}
              />
              <Tile
                name="最终审核通过率"
                value={percent(d.quality.final_approval_rate)}
                note={`${d.quality.final_approved_shares} / ${d.quality.final_reviewed_shares} 份`}
              />
            </div>
          </Panel>
          <Panel title="6. 审核时效">
            <div className="grid grid-cols-3 gap-3">
              <Tile
                name="审核尝试样本数"
                value={d.review_latency.sample_attempts}
              />
              <Tile
                name="中位耗时"
                value={duration(d.review_latency.median_seconds)}
              />
              <Tile
                name="P90 耗时"
                value={duration(d.review_latency.p90_seconds)}
              />
            </div>
          </Panel>
          <Panel title="7. 会员任务奖励">
            <div className="grid grid-cols-2 gap-3">
              <Tile name="已入账份数" value={d.rewards.credited_shares} />
              <Tile
                name="已入账会员奖励"
                value={`¥${(d.rewards.member_task_reward_minor / 100).toFixed(2)}`}
              />
            </div>
          </Panel>
          <Panel title="8. 异常与待处理">
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
              <Tile name="逾期份数" value={d.exceptions.overdue_shares} />
              <Tile name="驳回份数" value={d.exceptions.rejected_shares} />
              <Tile name="取消份数" value={d.exceptions.cancelled_shares} />
              <Tile
                name="临近截止份数"
                value={d.exceptions.near_deadline_shares}
              />
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}
